/**
 * Native booking — pure slot math. No DB, no server-only imports, safe on
 * both sides of the wire.
 *
 * Availability is stored as a weekly pattern in the Strategist's own
 * timezone (weekday + minutes-from-midnight). Slots are materialised into
 * absolute UTC instants here; every timestamp that crosses the wire or
 * lands in Postgres is UTC. Display-local formatting happens in the UI.
 */

export interface AvailabilityWindow {
  id: string;
  agent_id: string;
  weekday: number; // 0 = Sunday
  start_minute: number;
  end_minute: number;
  slot_minutes: number;
  timezone: string;
}

export interface Slot {
  /** ISO-8601 UTC instant. */
  startsAt: string;
  endsAt: string;
  minutes: number;
}

/** Offset (ms) of `tz` from UTC at the given instant. */
function tzOffsetMs(instant: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(dtf.formatToParts(instant).map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === "24" ? "0" : parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - instant.getTime();
}

/** Wall-clock time in `tz` → the matching UTC instant. */
export function zonedWallToUtc(
  year: number,
  month: number,
  day: number,
  minutesFromMidnight: number,
  tz: string,
): Date {
  const naive = Date.UTC(year, month - 1, day, 0, minutesFromMidnight, 0, 0);
  // Two passes settles DST boundaries: the first offset is read at the naive
  // instant, the second at the corrected one.
  let utc = naive - tzOffsetMs(new Date(naive), tz);
  utc = naive - tzOffsetMs(new Date(utc), tz);
  return new Date(utc);
}

/** Calendar date + weekday of an instant, as seen in `tz`. */
export function zonedDateParts(instant: Date, tz: string) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(instant).map((x) => [x.type, x.value]));
  const WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    weekday: WD[p.weekday as string] ?? 0,
  };
}

export interface GenerateOptions {
  /** Days of runway to publish, starting today in the Strategist's timezone. */
  days?: number;
  /** Slots closer than this many hours are not offered. */
  leadTimeHours?: number;
  now?: Date;
}

/**
 * Expand weekly windows into concrete UTC slots, minus anything already
 * taken and anything in the past / inside the lead-time buffer.
 */
export function generateSlots(
  windows: AvailabilityWindow[],
  takenIsoStarts: string[],
  opts: GenerateOptions = {},
): Slot[] {
  const days = opts.days ?? 21;
  const leadMs = (opts.leadTimeHours ?? 4) * 3600_000;
  const now = opts.now ?? new Date();
  const floor = now.getTime() + leadMs;
  const taken = new Set(takenIsoStarts.map((t) => new Date(t).getTime()));

  const out: Slot[] = [];
  for (const w of windows) {
    const tz = w.timezone || "UTC";
    for (let d = 0; d < days; d++) {
      const cursor = new Date(now.getTime() + d * 86_400_000);
      const parts = zonedDateParts(cursor, tz);
      if (parts.weekday !== w.weekday) continue;

      for (let m = w.start_minute; m + w.slot_minutes <= w.end_minute; m += w.slot_minutes) {
        const start = zonedWallToUtc(parts.year, parts.month, parts.day, m, tz);
        if (start.getTime() < floor) continue;
        if (taken.has(start.getTime())) continue;
        out.push({
          startsAt: start.toISOString(),
          endsAt: new Date(start.getTime() + w.slot_minutes * 60_000).toISOString(),
          minutes: w.slot_minutes,
        });
      }
    }
  }

  const seen = new Set<string>();
  return out
    .filter((s) => (seen.has(s.startsAt) ? false : (seen.add(s.startsAt), true)))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/** Group slots by calendar day in the viewer's timezone. */
export function groupByLocalDay(slots: Slot[], tz?: string) {
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const map = new Map<string, { label: string; slots: Slot[] }>();
  for (const s of slots) {
    const d = new Date(s.startsAt);
    const key = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    const label = new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "long", month: "long", day: "numeric" }).format(d);
    if (!map.has(key)) map.set(key, { label, slots: [] });
    map.get(key)!.slots.push(s);
  }
  return Array.from(map.entries()).map(([key, v]) => ({ key, ...v }));
}

export function formatSlotTime(iso: string, tz?: string) {
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}

export function formatSlotFull(iso: string, tz?: string) {
  const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    weekday: "long", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(new Date(iso));
}

export const WEEKDAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

export function minutesToLabel(m: number) {
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${ampm}`;
}
